import React, { createContext, useContext, useReducer, useCallback, useRef, useMemo } from 'react';

/**
 * Custom Redux-like Store for Performance Optimization
 * Features:
 * - Centralized state management
 * - Built-in caching with TTL
 * - Optimistic updates
 * - API call deduplication
 * - Automatic cache invalidation
 */

// Initial State
const initialState = {
  // User data
  user: null,

  // Posts data
  posts: {
    'My Posts': { data: [], loading: false, error: null, lastFetch: null },
    'My Network': { data: [], loading: false, error: null, lastFetch: null },
    'Around Me': { data: [], loading: false, error: null, lastFetch: null },
  },

  // Vessels data
  vessels: { data: [], loading: false, error: null, lastFetch: null },

  // Geolocation data
  geolocation: { latitude: null, longitude: null, lastFetch: null },

  // Referral data
  referralCheck: { data: null, loading: false, error: null, lastFetch: null },

  // Friend suggestions data
  friendSuggestions: { data: [], loading: false, error: null, lastFetch: null, totalPages: 1 },

  // Weather data
  weather: {
    data: null,
    location: 'Fetching location...',
    latLong: { lat: null, long: null },
    loading: false,
    error: null,
    lastFetch: null
  },

  // UI state
  ui: {
    toggleOption: localStorage.getItem("portfolioToggleOption") || "Around Me",
    currentPage: 1,
    hasMorePosts: true,
    loadingMore: false,
  }
};

// Action Types
export const ActionTypes = {
  // User actions
  SET_USER: 'SET_USER',

  // Posts actions
  SET_POSTS: 'SET_POSTS',
  SET_POSTS_LOADING: 'SET_POSTS_LOADING',
  SET_POSTS_ERROR: 'SET_POSTS_ERROR',
  ADD_POST_OPTIMISTIC: 'ADD_POST_OPTIMISTIC',
  UPDATE_POST_OPTIMISTIC: 'UPDATE_POST_OPTIMISTIC',
  DELETE_POST_OPTIMISTIC: 'DELETE_POST_OPTIMISTIC',
  APPEND_POSTS: 'APPEND_POSTS',

  // Vessels actions
  SET_VESSELS: 'SET_VESSELS',
  SET_VESSELS_LOADING: 'SET_VESSELS_LOADING',

  // Geolocation actions
  SET_GEOLOCATION: 'SET_GEOLOCATION',

  // Referral actions
  SET_REFERRAL_CHECK: 'SET_REFERRAL_CHECK',
  SET_REFERRAL_LOADING: 'SET_REFERRAL_LOADING',

  // Friend suggestions actions
  SET_FRIEND_SUGGESTIONS: 'SET_FRIEND_SUGGESTIONS',
  SET_FRIEND_SUGGESTIONS_LOADING: 'SET_FRIEND_SUGGESTIONS_LOADING',
  SET_FRIEND_SUGGESTIONS_ERROR: 'SET_FRIEND_SUGGESTIONS_ERROR',

  // Weather actions
  SET_WEATHER: 'SET_WEATHER',
  SET_WEATHER_LOADING: 'SET_WEATHER_LOADING',
  SET_WEATHER_ERROR: 'SET_WEATHER_ERROR',
  SET_WEATHER_LOCATION: 'SET_WEATHER_LOCATION',

  // UI actions
  SET_TOGGLE_OPTION: 'SET_TOGGLE_OPTION',
  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_HAS_MORE_POSTS: 'SET_HAS_MORE_POSTS',
  SET_LOADING_MORE: 'SET_LOADING_MORE',

  // Cache actions
  INVALIDATE_CACHE: 'INVALIDATE_CACHE',
  CLEAR_CACHE: 'CLEAR_CACHE',
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_USER:
      return {
        ...state,
        user: action.payload,
      };

    case ActionTypes.SET_POSTS:
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.toggleOption]: {
            data: action.payload,
            loading: false,
            error: null,
            lastFetch: Date.now(),
          },
        },
      };

    case ActionTypes.SET_POSTS_LOADING:
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.toggleOption]: {
            ...state.posts[action.toggleOption],
            loading: action.payload,
          },
        },
      };

    case ActionTypes.SET_POSTS_ERROR:
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.toggleOption]: {
            ...state.posts[action.toggleOption],
            loading: false,
            error: action.payload,
          },
        },
      };

    case ActionTypes.ADD_POST_OPTIMISTIC:
      return {
        ...state,
        posts: {
          ...state.posts,
          'My Posts': {
            ...state.posts['My Posts'],
            data: [action.payload, ...state.posts['My Posts'].data],
          },
        },
      };

    case ActionTypes.UPDATE_POST_OPTIMISTIC:
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.toggleOption]: {
            ...state.posts[action.toggleOption],
            data: state.posts[action.toggleOption].data.map(post =>
              post.id === action.payload.id ? { ...post, ...action.payload } : post
            ),
          },
        },
      };

    case ActionTypes.DELETE_POST_OPTIMISTIC:
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.toggleOption]: {
            ...state.posts[action.toggleOption],
            data: state.posts[action.toggleOption].data.filter(
              post => post.id !== action.payload
            ),
          },
        },
      };

    case ActionTypes.APPEND_POSTS:
      return {
        ...state,
        posts: {
          ...state.posts,
          [action.toggleOption]: {
            data: [...state.posts[action.toggleOption].data, ...action.payload],
            loading: false,
            error: null,
            lastFetch: Date.now(),
          },
        },
      };

    case ActionTypes.SET_VESSELS:
      return {
        ...state,
        vessels: {
          data: action.payload,
          loading: false,
          error: null,
          lastFetch: Date.now(),
        },
      };

    case ActionTypes.SET_VESSELS_LOADING:
      return {
        ...state,
        vessels: {
          ...state.vessels,
          loading: action.payload,
        },
      };

    case ActionTypes.SET_GEOLOCATION:
      return {
        ...state,
        geolocation: {
          ...action.payload,
          lastFetch: Date.now(),
        },
      };

    case ActionTypes.SET_REFERRAL_CHECK:
      return {
        ...state,
        referralCheck: {
          data: action.payload,
          loading: false,
          error: null,
          lastFetch: Date.now(),
        },
      };

    case ActionTypes.SET_REFERRAL_LOADING:
      return {
        ...state,
        referralCheck: {
          ...state.referralCheck,
          loading: action.payload,
        },
      };

    case ActionTypes.SET_FRIEND_SUGGESTIONS:
      return {
        ...state,
        friendSuggestions: {
          data: action.payload.data,
          loading: false,
          error: null,
          lastFetch: Date.now(),
          totalPages: action.payload.totalPages || 1,
        },
      };

    case ActionTypes.SET_FRIEND_SUGGESTIONS_LOADING:
      return {
        ...state,
        friendSuggestions: {
          ...state.friendSuggestions,
          loading: action.payload,
        },
      };

    case ActionTypes.SET_FRIEND_SUGGESTIONS_ERROR:
      return {
        ...state,
        friendSuggestions: {
          ...state.friendSuggestions,
          loading: false,
          error: action.payload,
        },
      };

    case ActionTypes.SET_WEATHER:
      return {
        ...state,
        weather: {
          ...state.weather,
          data: action.payload.data,
          latLong: action.payload.latLong || state.weather.latLong,
          loading: false,
          error: null,
          lastFetch: Date.now(),
        },
      };

    case ActionTypes.SET_WEATHER_LOADING:
      return {
        ...state,
        weather: {
          ...state.weather,
          loading: action.payload,
        },
      };

    case ActionTypes.SET_WEATHER_ERROR:
      return {
        ...state,
        weather: {
          ...state.weather,
          loading: false,
          error: action.payload,
        },
      };

    case ActionTypes.SET_WEATHER_LOCATION:
      return {
        ...state,
        weather: {
          ...state.weather,
          location: action.payload,
        },
      };

    case ActionTypes.SET_TOGGLE_OPTION:
      localStorage.setItem("portfolioToggleOption", action.payload);
      return {
        ...state,
        ui: {
          ...state.ui,
          toggleOption: action.payload,
          currentPage: 1,
          hasMorePosts: true,
        },
      };

    case ActionTypes.SET_CURRENT_PAGE:
      return {
        ...state,
        ui: {
          ...state.ui,
          currentPage: action.payload,
        },
      };

    case ActionTypes.SET_HAS_MORE_POSTS:
      return {
        ...state,
        ui: {
          ...state.ui,
          hasMorePosts: action.payload,
        },
      };

    case ActionTypes.SET_LOADING_MORE:
      return {
        ...state,
        ui: {
          ...state.ui,
          loadingMore: action.payload,
        },
      };

    case ActionTypes.INVALIDATE_CACHE:
      return {
        ...state,
        [action.payload]: {
          ...state[action.payload],
          lastFetch: null,
        },
      };

    case ActionTypes.CLEAR_CACHE:
      return {
        ...initialState,
        user: state.user, // Preserve user data
      };

    default:
      return state;
  }
};

// Create Context
const AppStoreContext = createContext();

// Cache Configuration
const CACHE_CONFIG = {
  posts: 5 * 60 * 1000, // 5 minutes
  vessels: 10 * 60 * 1000, // 10 minutes
  geolocation: 60 * 60 * 1000, // 1 hour
  referralCheck: 10 * 60 * 1000, // 10 minutes
  friendSuggestions: 5 * 60 * 1000, // 5 minutes
  weather: 15 * 60 * 1000, // 15 minutes
};

// Provider Component
export const AppStoreProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const pendingRequests = useRef(new Map());

  // Helper function to check if cache is valid
  const isCacheValid = useCallback((cacheKey, lastFetch) => {
    if (!lastFetch) return false;
    const ttl = CACHE_CONFIG[cacheKey] || 0;
    return Date.now() - lastFetch < ttl;
  }, []);

  // Helper function to deduplicate API calls
  const deduplicateRequest = useCallback((key, apiCall) => {
    // If there's already a pending request for this key, return it
    if (pendingRequests.current.has(key)) {
      return pendingRequests.current.get(key);
    }

    // Create new request
    const promise = apiCall()
      .finally(() => {
        // Remove from pending requests when complete
        pendingRequests.current.delete(key);
      });

    // Store in pending requests
    pendingRequests.current.set(key, promise);
    return promise;
  }, []);

  // Actions object - memoized to maintain stable reference across renders.
  // dispatch from useReducer is stable, so this object is created once.
  const actions = useMemo(() => ({
    // User actions
    setUser: (user) => {
      dispatch({ type: ActionTypes.SET_USER, payload: user });
    },

    // Posts actions
    setPosts: (toggleOption, posts) => {
      dispatch({ type: ActionTypes.SET_POSTS, payload: posts, toggleOption });
    },

    setPostsLoading: (toggleOption, loading) => {
      dispatch({ type: ActionTypes.SET_POSTS_LOADING, payload: loading, toggleOption });
    },

    setPostsError: (toggleOption, error) => {
      dispatch({ type: ActionTypes.SET_POSTS_ERROR, payload: error, toggleOption });
    },

    addPostOptimistic: (post) => {
      dispatch({ type: ActionTypes.ADD_POST_OPTIMISTIC, payload: post });
    },

    updatePostOptimistic: (toggleOption, post) => {
      dispatch({ type: ActionTypes.UPDATE_POST_OPTIMISTIC, payload: post, toggleOption });
    },

    deletePostOptimistic: (toggleOption, postId) => {
      dispatch({ type: ActionTypes.DELETE_POST_OPTIMISTIC, payload: postId, toggleOption });
    },

    appendPosts: (toggleOption, posts) => {
      dispatch({ type: ActionTypes.APPEND_POSTS, payload: posts, toggleOption });
    },

    // Vessels actions
    setVessels: (vessels) => {
      dispatch({ type: ActionTypes.SET_VESSELS, payload: vessels });
    },

    setVesselsLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_VESSELS_LOADING, payload: loading });
    },

    // Geolocation actions
    setGeolocation: (lat, lng) => {
      dispatch({ type: ActionTypes.SET_GEOLOCATION, payload: { latitude: lat, longitude: lng } });
    },

    // Referral actions
    setReferralCheck: (data) => {
      dispatch({ type: ActionTypes.SET_REFERRAL_CHECK, payload: data });
    },

    setReferralLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_REFERRAL_LOADING, payload: loading });
    },

    // Friend suggestions actions
    setFriendSuggestions: (data, totalPages) => {
      dispatch({ type: ActionTypes.SET_FRIEND_SUGGESTIONS, payload: { data, totalPages } });
    },

    setFriendSuggestionsLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_FRIEND_SUGGESTIONS_LOADING, payload: loading });
    },

    setFriendSuggestionsError: (error) => {
      dispatch({ type: ActionTypes.SET_FRIEND_SUGGESTIONS_ERROR, payload: error });
    },

    // Weather actions
    setWeather: (data, latLong) => {
      dispatch({ type: ActionTypes.SET_WEATHER, payload: { data, latLong } });
    },

    setWeatherLoading: (loading) => {
      dispatch({ type: ActionTypes.SET_WEATHER_LOADING, payload: loading });
    },

    setWeatherError: (error) => {
      dispatch({ type: ActionTypes.SET_WEATHER_ERROR, payload: error });
    },

    setWeatherLocation: (location) => {
      dispatch({ type: ActionTypes.SET_WEATHER_LOCATION, payload: location });
    },

    // UI actions
    setToggleOption: (option) => {
      dispatch({ type: ActionTypes.SET_TOGGLE_OPTION, payload: option });
    },

    setCurrentPage: (page) => {
      dispatch({ type: ActionTypes.SET_CURRENT_PAGE, payload: page });
    },

    setHasMorePosts: (hasMore) => {
      dispatch({ type: ActionTypes.SET_HAS_MORE_POSTS, payload: hasMore });
    },

    setLoadingMore: (loading) => {
      dispatch({ type: ActionTypes.SET_LOADING_MORE, payload: loading });
    },

    // Cache actions
    invalidateCache: (cacheKey) => {
      dispatch({ type: ActionTypes.INVALIDATE_CACHE, payload: cacheKey });
    },

    clearCache: () => {
      dispatch({ type: ActionTypes.CLEAR_CACHE });
    },
  }), [dispatch]);

  // ⚡ OPTIMIZATION: Memoize value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    state,
    actions,
    isCacheValid,
    deduplicateRequest,
  }), [state, actions, isCacheValid, deduplicateRequest]);

  return (
    <AppStoreContext.Provider value={value}>
      {children}
    </AppStoreContext.Provider>
  );
};

// Custom Hook to use the store
export const useAppStore = () => {
  const context = useContext(AppStoreContext);
  if (!context) {
    throw new Error('useAppStore must be used within AppStoreProvider');
  }
  return context;
};

export default AppStoreContext;
